import React, { useEffect } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { checkRemoteVersion } from "./checkRemoteVersion";

/**
 * High-Order Component (HOC): withThemesAndRemoteVersionCheck
 * ------------------------------------------------------------
 * Purpose:
 * - Wraps remote-federated pages with both MUI and styled-components themes.
 * - Automatically performs a version check for the given remote app on mount.
 *
 * Why:
 * - Ensures consistent theming across federated micro-frontends.
 * - Detects stale or updated remotes at runtime and refreshes if needed.
 *
 * Usage:
 *   const WrappedPage = withThemesAndRemoteVersionCheck(MyPage, theme, muiTheme, "rateShop");
 *
 * Notes:
 * - Called each time a federated page mounts (so version check re-runs on navigation).
 * - Non-blocking: version check runs async; user won't see delay.
 */
export function withThemesAndRemoteVersionCheck(
  Page: React.ComponentType<any>,
  theme: any,
  muiTheme: any,
  scope: string,
) {
  const ThemedPage: React.FC<any> = (props) => {
    // run once per mount (i.e., whenever user lands on this page/route)
    useEffect(() => {
      if (scope) checkRemoteVersion(scope);
    }, [scope]);

    if (muiTheme && theme) {
      return (
        <MuiThemeProvider theme={muiTheme}>
          <StyledThemeProvider theme={theme}>
            <Page {...props} />
          </StyledThemeProvider>
        </MuiThemeProvider>
      );
    }

    if (muiTheme) {
      return (
        <MuiThemeProvider theme={muiTheme}>
          <Page {...props} />
        </MuiThemeProvider>
      );
    }

    if (theme) {
      return (
        <StyledThemeProvider theme={theme}>
          <Page {...props} />
        </StyledThemeProvider>
      );
    }

    return <Page {...props} />;
  };

  return ThemedPage;
}

// --- Example usage ---
const OrderEntryPage = React.lazy(async () => {
  await checkRemoteVersion("orderManagement");
  // @ts-ignore: remote module provided at runtime via Module Federation
  const mod = await import("orderManagement/plugin");
  const Page = mod?.pages?.OrderEntryPage;
  const { theme = null, muiTheme = null } = mod?.theming ?? {};
  return {
    default: withThemesAndRemoteVersionCheck(
      Page,
      theme,
      muiTheme,
      "orderManagement",
    ),
  };
});
const OrderSummaryPage = React.lazy(async () => {
  await checkRemoteVersion("orderManagement");
  // @ts-ignore
  const mod = await import("orderManagement/plugin");
  const Page = mod?.pages?.OrderSummaryPage;
  const { theme = null, muiTheme = null } = mod?.theming ?? {};
  return {
    default: withThemesAndRemoteVersionCheck(
      Page,
      theme,
      muiTheme,
      "orderManagement",
    ),
  };
});

const AdminPage = React.lazy(async () => {
  await checkRemoteVersion("orderManagement");
  // @ts-ignore
  const mod = await import("orderManagement/plugin");
  const Page = mod?.pages?.AdminPage;
  const { theme = null, muiTheme = null } = mod?.theming ?? {};
  return {
    default: withThemesAndRemoteVersionCheck(
      Page,
      theme,
      muiTheme,
      "orderManagement",
    ),
  };
});

const LoadBoardPage = React.lazy(async () => {
  await checkRemoteVersion("orderManagement");
  // @ts-ignore
  const mod = await import("orderManagement/plugin");
  const Page = mod?.pages?.LoadSummaryPage;
  const { theme = null, muiTheme = null } = mod?.theming ?? {};
  return {
    default: withThemesAndRemoteVersionCheck(
      Page,
      theme,
      muiTheme,
      "orderManagement",
    ),
  };
});

// --- RateShop pages ---
const RateRequestEntryPage = React.lazy(async () => {
  await checkRemoteVersion("rateShop");
  // @ts-ignore
  const mod = await import("rateShop/plugin");
  const Page = mod?.pages?.RateRequestEntryPage;
  const { theme = null, muiTheme = null } = mod?.theming ?? {};
  return {
    default: withThemesAndRemoteVersionCheck(Page, theme, muiTheme, "rateShop"),
  };
});

const QuoteSummary = React.lazy(async () => {
  await checkRemoteVersion("rateShop");
  // @ts-ignore
  const mod = await import("rateShop/plugin");
  const Page = mod?.pages?.QuoteSummary;
  const { theme = null, muiTheme = null } = mod?.theming ?? {};
  return {
    default: withThemesAndRemoteVersionCheck(Page, theme, muiTheme, "rateShop"),
  };
});

const CustomerInvoiceSummaryPage = React.lazy(async () => {
  await checkRemoteVersion("invoiceManagement");
  // @ts-ignore: remote module provided at runtime via Module Federation
  const mod = await import("invoiceManagement/plugin");
  const Page = mod?.pages?.CustomerInvoiceSummaryPage;
  const { theme = null, muiTheme = null } = mod?.theming ?? {};
  return {
    default: withThemesAndRemoteVersionCheck(
      Page,
      theme,
      muiTheme,
      "invoiceManagement",
    ),
  };
});

export {
  OrderEntryPage,
  OrderSummaryPage,
  LoadBoardPage,
  AdminPage,
  RateRequestEntryPage,
  QuoteSummary,
  // AccountsReceivableAuditPage,
  // AccountsPayablePage,
  // ListBillReportPage,
  // ARReportPage,
  CustomerInvoiceSummaryPage,
};
